import { useCallback, useEffect, useState } from 'react';
import { Activity, ChevronDown, ChevronRight, Clock, Download, FileSpreadsheet, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Loader } from '@/components/ui/Loader';
import { StatTile } from '@/components/ui/StatTile';
import { useModuleAccess } from '@/lib/useModuleAccess';
import { loadPdfAssets } from '@/lib/pdf/pdfAssets';
import { STANDARD_RATES, callCost, formatRand } from '@/lib/callCosts';
import { dispositionBucket } from '@/utils/callRecords';
import { formatDurationHMS, partyText } from '@/lib/reports/callCostReport';
import {
  activityTimeFormatter,
  dailyActivityCsvs,
  dailyActivityPdf,
  describeFollowUp,
  loadDailyActivity,
  missedStatus,
  recordTimeMs,
  type DailyActivity,
} from '@/lib/reports/dailyActivityReport';
import { formatIsoDate, resolveDateRange, type DateRangeKey } from '@/lib/reports/schedule';
import { ScheduleReportButton } from './ScheduleReportButton';

/**
 * Daily Activity: today's (or any period's) calls for every extension, as on the dashboard, then
 * the missed calls and whether they were returned, then each extension's calls and what the calls
 * made cost.
 */

const PRESETS: Array<{ id: DateRangeKey; label: string }> = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'thisWeek', label: 'This week' },
  { id: 'lastWeek', label: 'Last week' },
  { id: 'thisMonth', label: 'This month' },
  { id: 'lastMonth', label: 'Last month' },
];

const th = 'px-3 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 whitespace-nowrap';
const td = 'px-3 py-2.5 text-sm text-gray-900 dark:text-gray-100';
const minutes = (seconds: number) => `${Math.round(seconds / 6) / 10} min`;

function download(text: string, fileName: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

function parseRate(value: string) {
  const rate = Number(value.replace(',', '.'));
  return Number.isFinite(rate) && rate >= 0 ? rate : 0;
}

export function DailyActivityReport() {
  const { session } = useModuleAccess('reporting');
  const [preset, setPreset] = useState<DateRangeKey | null>('today');
  const [startDate, setStartDate] = useState(() => formatIsoDate(new Date()));
  const [endDate, setEndDate] = useState(() => formatIsoDate(new Date()));
  const [mobileRate, setMobileRate] = useState(STANDARD_RATES.mobilePerMinute.toFixed(2));
  const [landlineRate, setLandlineRate] = useState(STANDARD_RATES.landlinePerMinute.toFixed(2));
  const [data, setData] = useState<DailyActivity | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [openExtensions, setOpenExtensions] = useState<Set<string>>(new Set());

  const rates = { mobilePerMinute: parseRate(mobileRate), landlinePerMinute: parseRate(landlineRate) };

  const generate = useCallback(
    async (from: string, to: string) => {
      if (!from || !to || from > to) {
        toast.error('Choose a start date on or before the end date');
        return;
      }
      setLoading(true);
      try {
        setData(await loadDailyActivity(from, to, rates));
        setOpenExtensions(new Set());
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Could not load the calls');
      } finally {
        setLoading(false);
      }
    },
    // Rates are read when generating
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mobileRate, landlineRate]
  );

  useEffect(() => {
    const today = formatIsoDate(new Date());
    void generate(today, today);
    // Today, once, when the tab opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const choosePreset = (id: DateRangeKey) => {
    const range = resolveDateRange(id, new Date());
    setPreset(id);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    void generate(range.startDate, range.endDate);
  };

  const fileBase = data ? `Daily_Activity_${data.startDate}${data.endDate !== data.startDate ? `_to_${data.endDate}` : ''}` : '';

  const exportPdf = async () => {
    if (!data) return;
    setExporting(true);
    try {
      dailyActivityPdf(data, { customerName: session?.customer?.name, assets: await loadPdfAssets(), includeCallLists: true }).save(`${fileBase}.pdf`);
    } catch (error) {
      console.error(error);
      toast.error('Could not make the PDF');
    } finally {
      setExporting(false);
    }
  };

  const formatTime = data ? activityTimeFormatter(data.startDate, data.endDate) : () => '';
  const toggleExtension = (extension: string) =>
    setOpenExtensions((current) => {
      const next = new Set(current);
      if (next.has(extension)) next.delete(extension);
      else next.add(extension);
      return next;
    });

  const inputClass =
    'px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white dark:[color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-primary-500';

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-xl">
              <Activity className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Daily Activity</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Every extension’s calls, the missed calls and whether they were returned, and what the calls made cost
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <ScheduleReportButton
              kind="activity"
              filters={{ mobileRate: rates.mobilePerMinute, landlineRate: rates.landlinePerMinute, includeCallLists: true }}
              dateRange={preset ?? undefined}
            />
            <Button size="sm" variant="secondary" disabled={!data} onClick={() => data && download(dailyActivityCsvs(data).extensions, `${fileBase}_by_extension.csv`)}>
              <FileSpreadsheet className="w-4 h-4" />
              CSV
            </Button>
            <Button size="sm" variant="secondary" disabled={!data} onClick={() => data && download(dailyActivityCsvs(data).missed, `${fileBase}_missed_calls.csv`)}>
              <FileSpreadsheet className="w-4 h-4" />
              Missed CSV
            </Button>
            <Button size="sm" onClick={exportPdf} disabled={!data} isLoading={exporting}>
              <Download className="w-4 h-4" />
              PDF
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => choosePreset(item.id)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  preset === item.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-panel dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
          <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
            From
            <input type="date" value={startDate} onChange={(e) => { setPreset(null); setStartDate(e.target.value); }} className={inputClass} />
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
            To
            <input type="date" value={endDate} onChange={(e) => { setPreset(null); setEndDate(e.target.value); }} className={inputClass} />
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
            Mobile R/min
            <input type="number" min="0" step="0.01" value={mobileRate} onChange={(e) => setMobileRate(e.target.value)} className={`${inputClass} w-24`} />
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
            Landline R/min
            <input type="number" min="0" step="0.01" value={landlineRate} onChange={(e) => setLandlineRate(e.target.value)} className={`${inputClass} w-24`} />
          </label>
          <Button onClick={() => void generate(startDate, endDate)} isLoading={loading}>
            <RefreshCw className="w-4 h-4" />
            Generate
          </Button>
        </div>
      </Card>

      {loading ? (
        <Card>
          <Loader text="Loading calls..." />
        </Card>
      ) : data ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
            <StatTile featured size="md" label="Total calls" value={data.totals.total} caption="Received + made" />
            <StatTile size="md" label="Received" value={data.totals.received} caption="Inbound calls" />
            <StatTile size="md" label="Made" value={data.totals.made} caption="Outbound calls" />
            <StatTile
              size="md"
              label="Missed"
              value={data.totals.missed}
              dotClassName="bg-red-500"
              caption={`${data.totals.missedAtExtensions} at extensions · ${data.totals.missedInQueues} in queues`}
            />
            <div className="sm:col-span-2 xl:col-span-1 min-w-0">
              <StatTile
                size="md"
                label="Talk time"
                value={<span className="text-2xl">{formatDurationHMS(data.totals.talkSeconds)}</span>}
                icon={Clock}
                caption="Total time on calls"
              />
            </div>
          </div>

          <Card>
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Missed calls</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {data.missedCalls.length} call{data.missedCalls.length === 1 ? '' : 's'} ·{' '}
                {data.missedCalls.filter((call) => call.followUp.kind === 'none').length} not called back
              </p>
            </div>
            {data.missedCalls.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No missed calls.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-panel dark:bg-gray-900/60">
                    <tr>
                      <th className={th}>{data.startDate === data.endDate ? 'Time' : 'When'}</th>
                      <th className={th}>Caller</th>
                      <th className={th}>Missed by</th>
                      <th className={th}>Result</th>
                      <th className={`${th} text-right`}>Ring</th>
                      <th className={th}>Follow-up</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {data.missedCalls.map((call, index) => (
                      <tr key={`${call.atMs}-${index}`}>
                        <td className={`${td} whitespace-nowrap tabular-nums`}>{formatTime(call.atMs)}</td>
                        <td className={`${td} font-mono`}>{call.caller}</td>
                        <td className={td}>{call.missedBy}</td>
                        <td className={td}>
                          <span
                            className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              call.queue ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                            }`}
                          >
                            {missedStatus(call.record, call.queue)}
                          </span>
                        </td>
                        <td className={`${td} text-right tabular-nums`}>{Math.round(call.record.ring_duration || 0)}s</td>
                        <td
                          className={`${td} ${
                            call.followUp.kind === 'none'
                              ? 'font-semibold text-red-700 dark:text-red-400'
                              : call.followUp.kind === 'unknownCaller'
                                ? 'text-gray-500'
                                : 'text-primary-700 dark:text-primary-400'
                          }`}
                        >
                          {describeFollowUp(call.followUp, formatTime)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card>
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">By extension</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {data.activity.length} with calls{data.quietExtensions ? ` · ${data.quietExtensions} with none` : ''}
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-panel dark:bg-gray-900/60">
                  <tr>
                    <th className={th}>Extension</th>
                    <th className={`${th} text-right`}>Received</th>
                    <th className={`${th} text-right`}>Missed</th>
                    <th className={`${th} text-right`}>Made</th>
                    <th className={`${th} text-right`}>Total</th>
                    <th className={`${th} text-right`}>Talk time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {data.activity.map((row) => (
                    <tr key={row.ext_num}>
                      <td className={td}>
                        <span className="font-medium">{row.ext_num}</span>
                        {row.ext_name && row.ext_name !== row.ext_num && <span className="text-gray-500 dark:text-gray-400"> - {row.ext_name}</span>}
                      </td>
                      <td className={`${td} text-right tabular-nums`}>{row.received}</td>
                      <td className={`${td} text-right tabular-nums ${row.missed ? 'text-red-600 dark:text-red-400 font-medium' : ''}`}>{row.missed}</td>
                      <td className={`${td} text-right tabular-nums`}>{row.made}</td>
                      <td className={`${td} text-right tabular-nums font-semibold`}>{row.received + row.made}</td>
                      <td className={`${td} text-right tabular-nums`}>{formatDurationHMS(row.duration)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-primary-50 dark:bg-primary-900/20 font-semibold">
                    <td className={td}>Total</td>
                    <td className={`${td} text-right tabular-nums`}>{data.totals.received}</td>
                    <td className={`${td} text-right tabular-nums`}>{data.totals.missedAtExtensions}</td>
                    <td className={`${td} text-right tabular-nums`}>{data.totals.made}</td>
                    <td className={`${td} text-right tabular-nums`}>{data.totals.total}</td>
                    <td className={`${td} text-right tabular-nums`}>{formatDurationHMS(data.totals.talkSeconds)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>

          <Card>
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Calls made</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Mobile {formatRand(data.rates.mobilePerMinute)}/min · landline {formatRand(data.rates.landlinePerMinute)}/min · billed per second ·
                click an extension for its calls
              </p>
            </div>
            {data.made.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No calls made.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-panel dark:bg-gray-900/60">
                    <tr>
                      <th className={th}>Extension</th>
                      <th className={`${th} text-right`}>Calls</th>
                      <th className={`${th} text-right`}>Answered</th>
                      <th className={`${th} text-right`}>Minutes</th>
                      <th className={`${th} text-right`}>Mobile</th>
                      <th className={`${th} text-right`}>Landline</th>
                      <th className={`${th} text-right`}>Cost</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {data.made.map((row) => {
                      const open = openExtensions.has(row.extension);
                      return [
                        <tr
                          key={row.extension}
                          onClick={() => toggleExtension(row.extension)}
                          className="cursor-pointer hover:bg-primary-50/50 dark:hover:bg-primary-900/10"
                        >
                          <td className={`${td} font-medium`}>
                            <span className="inline-flex items-center gap-1">
                              {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                              {row.label}
                            </span>
                          </td>
                          <td className={`${td} text-right tabular-nums`}>{row.calls}</td>
                          <td className={`${td} text-right tabular-nums`}>{row.answered}</td>
                          <td className={`${td} text-right tabular-nums`}>{minutes(row.talkSeconds)}</td>
                          <td className={`${td} text-right tabular-nums`}>{formatRand(row.mobile.cost)}</td>
                          <td className={`${td} text-right tabular-nums`}>{formatRand(row.landline.cost)}</td>
                          <td className={`${td} text-right tabular-nums font-semibold`}>{formatRand(row.cost)}</td>
                        </tr>,
                        open && (
                          <tr key={`${row.extension}-calls`}>
                            <td colSpan={7} className="bg-panel/60 dark:bg-gray-900/40 px-3 py-2">
                              <table className="w-full text-left">
                                <tbody className="divide-y divide-gray-200/70 dark:divide-gray-700">
                                  {row.records.map((record, index) => {
                                    const cost = callCost(record, data.rates);
                                    const bucket = dispositionBucket(record);
                                    return (
                                      <tr key={index} className="text-sm">
                                        <td className="py-1.5 pr-3 tabular-nums whitespace-nowrap text-gray-600 dark:text-gray-300">{formatTime(recordTimeMs(record))}</td>
                                        <td className="py-1.5 pr-3 font-mono text-gray-900 dark:text-gray-100">{partyText(record.call_to)}</td>
                                        <td className={`py-1.5 pr-3 ${bucket === 'answered' ? 'text-primary-700 dark:text-primary-400' : 'text-orange-700 dark:text-orange-400'}`}>
                                          {record.disposition || 'Unknown'}
                                        </td>
                                        <td className="py-1.5 pr-3 text-right tabular-nums">{formatDurationHMS(Math.max(0, Math.round(record.talk_duration || 0)))}</td>
                                        <td className="py-1.5 text-right tabular-nums">{cost.category ? formatRand(cost.cost) : '-'}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        ),
                      ];
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-primary-50 dark:bg-primary-900/20 font-semibold">
                      <td className={td}>Total</td>
                      <td className={`${td} text-right tabular-nums`}>{data.madeTotals.calls}</td>
                      <td className={`${td} text-right tabular-nums`}>{data.madeTotals.answered}</td>
                      <td className={`${td} text-right tabular-nums`}>{minutes(data.madeTotals.talkSeconds)}</td>
                      <td className={`${td} text-right tabular-nums`}>{formatRand(data.madeTotals.mobile)}</td>
                      <td className={`${td} text-right tabular-nums`}>{formatRand(data.madeTotals.landline)}</td>
                      <td className={`${td} text-right tabular-nums text-primary-700 dark:text-primary-400`}>{formatRand(data.madeTotals.cost)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Card>
        </>
      ) : null}
    </div>
  );
}
